import React, { useEffect, useState } from 'react';
import { ColumnHeader } from '@deque/cauldron-react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Button, TextField } from '@deque/cauldron-react';
import type { v2 } from '@deque/billing-service-client';
import { useAuthContext } from '../../../../../common/contexts/auth';
import styles from '../jira-on-prem/JiraOnPremSettings.css';
import { type ErrorOrNull } from '../../../../../../typings/utils';
import ScrimmedLoader from '../../../../../common/components/ScrimmedLoader';
import { useHistory, useParams } from 'react-router-dom';
import {
  getIntegrationConnectionById,
  updateIntegrationConnection
} from '../../../../../common/utils/integrations-client/jira/connections';
import { getIntegrationProductInfoFromSlug } from '../../../../../common/utils/get-integration-product-name-from-slug';
import { ProductSlugs } from '@deque/billing-utils';
import { useGlobalToast } from '../../../../../common/contexts/globalToast';
import { validateName } from '../../../../utils/validate-integration-connection-settings';

export interface JiraDirectSettingsForm {
  name: string;
}

export interface JiraDirectSettingsProps {
  activeEnterprise: v2.Enterprise;
}

interface RouteParams {
  id: string;
}

function JiraDirectSettings({ activeEnterprise }: JiraDirectSettingsProps) {
  const { t } = useTranslation();
  const { setContents } = useGlobalToast();

  const history = useHistory();
  const { id: connection_id } = useParams<RouteParams>();

  // Get info required for making API calls
  const { user: authUser } = useAuthContext();

  // Get jira direct cloud settings for this enterprise
  const [jiraDirectSettingsError, setJiraDirectSettingsError] =
    useState<ErrorOrNull>(null);
  const [jiraDirectSettingsLoading, setJiraDirectSettingsLoading] =
    useState(true);

  useEffect(() => {
    async function fetchJiraDirectSettings() {
      if (!activeEnterprise || !authUser) {
        return;
      }

      try {
        setJiraDirectSettingsLoading(true);
        const integrationConnection = await getIntegrationConnectionById({
          enterpriseId: activeEnterprise.id,
          token: authUser.token,
          connectionId: connection_id
        });

        // Only reset if we have a name
        if (integrationConnection.name) {
          reset({
            name: integrationConnection.name
          });
        }
      } catch (error) {
        const errorMessage = t('Failed to load settings.');

        setJiraDirectSettingsError(new Error(errorMessage));
      } finally {
        setJiraDirectSettingsLoading(false);
      }
    }

    fetchJiraDirectSettings();
  }, [activeEnterprise, authUser]);

  useEffect(() => {
    if (!jiraDirectSettingsError) {
      return;
    }

    const toastMessage =
      jiraDirectSettingsError.message ||
      t('Failed to load settings information.');

    setContents(toastMessage, 'caution');
  }, [jiraDirectSettingsError]);

  const { handleSubmit, reset, control, formState } =
    useForm<JiraDirectSettingsForm>();

  const onSubmit = async (data: JiraDirectSettingsForm) => {
    if (!activeEnterprise || !authUser) {
      return;
    }

    try {
      setJiraDirectSettingsLoading(true);
      await updateIntegrationConnection({
        enterpriseId: activeEnterprise.id,
        connectionId: connection_id,
        token: authUser.token,
        body: {
          name: data.name.trim()
        }
      });

      goBackToIntegrationsConfiguration();
    } catch (error) {
      const errorMessage = t('Failed to save settings.');

      setJiraDirectSettingsError(new Error(errorMessage));
    } finally {
      setJiraDirectSettingsLoading(false);
    }
  };

  const goBackToIntegrationsConfiguration = () => {
    history.push('/configuration/integrations');
  };
  const productName = getIntegrationProductInfoFromSlug(
    ProductSlugs.jiraDirectCloudIntegration
  ).displayName;
  const productNameHeader = t('{{productName}} Settings', { productName });

  return (
    <>
      <ColumnHeader>
        <h2>{productNameHeader}</h2>
      </ColumnHeader>

      <h3 className={styles.productNameHeader}>{productNameHeader}</h3>

      {!jiraDirectSettingsLoading || (
        <ScrimmedLoader label={t('Loading ...')} />
      )}

      {!jiraDirectSettingsError && !jiraDirectSettingsLoading && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={styles.connectionSettingsForm}
        >
          <Controller
            control={control}
            name="name"
            rules={{ validate: (value: string) => validateName(value, t) }}
            render={({ field: { ref, ...field } }) => (
              <TextField
                {...field}
                fieldRef={ref}
                id="connection-name"
                aria-label={t('Connection name')}
                label={t('Connection name')}
                required
                requiredText={t('Required')}
                error={formState.errors.name?.message}
              />
            )}
          />

          <div className={styles.configurationFooter}>
            <Button onClick={handleSubmit(onSubmit)}>{t('Save')}</Button>
            <Button
              onClick={goBackToIntegrationsConfiguration}
              variant="secondary"
            >
              {t('Discard')}
            </Button>
          </div>
        </form>
      )}
    </>
  );
}

export default JiraDirectSettings;
